import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    CircularProgress,
    Alert,
    Box,
} from '@mui/material'

import { MedicalActTemplate, CreateMedicalActRecordDto } from '@/core/models/medical-act.model'
import {
    useGetMedicalActTemplatesQuery,
    useCreateMedicalActRecordMutation,
} from '@/core/services/medical-act'
import { useGetStaffsByEstablishmentQuery } from '@/core/services/staff'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { TemplateAutocomplete } from './TemplateAutocomplete'
import { SelectedTemplateInfo } from './SelectedTemplateInfo'
import { PerformerSelect } from './PerformerSelect'
import { ContextInfoBanner } from './ContextInfoBanner'

type RecordMedicalActModalProps = {
    open: boolean
    onClose: () => void
    patientId: string
    consultationId?: string
    expertiseId?: string
    onSuccess?: () => void
}

type FormData = {
    templateId: string
    performedById: string
    priceAtTime?: number
    notes: string
    performedAt: string
}

export function RecordMedicalActModal({
    open,
    onClose,
    patientId,
    consultationId,
    expertiseId,
    onSuccess,
}: RecordMedicalActModalProps) {
    const { t } = useTranslation()
    const authUser = useAppSelector(selectAuthUser)
    const establishmentId = authUser?.establishment?._id

    const [selectedTemplate, setSelectedTemplate] = useState<MedicalActTemplate | null>(null)

    const { data: templates, isLoading: isLoadingTemplates } = useGetMedicalActTemplatesQuery()
    const { data: staffList, isLoading: isLoadingStaff } = useGetStaffsByEstablishmentQuery(
        establishmentId || '',
        { skip: !establishmentId }
    )
    const [createRecord, { isLoading: isCreating, error: createError }] = useCreateMedicalActRecordMutation()

    const { control, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<FormData>({
        defaultValues: {
            templateId: '',
            performedById: authUser?._id || '',
            priceAtTime: undefined,
            notes: '',
            performedAt: new Date().toISOString().slice(0, 16),
        },
    })

    const watchedTemplateId = watch('templateId')

    // Update price when template changes
    useEffect(() => {
        if (watchedTemplateId && templates) {
            const template = templates.find(t => t._id === watchedTemplateId)
            if (template) {
                setSelectedTemplate(template)
                setValue('priceAtTime', template.price)
            }
        } else {
            setSelectedTemplate(null)
        }
    }, [watchedTemplateId, templates, setValue])

    // Reset form when dialog opens
    useEffect(() => {
        if (open) {
            reset({
                templateId: '',
                performedById: authUser?._id || '',
                priceAtTime: undefined,
                notes: '',
                performedAt: new Date().toISOString().slice(0, 16),
            })
            setSelectedTemplate(null)
        }
    }, [open, authUser, reset])

    const onSubmit = async (data: FormData) => {
        const dto: CreateMedicalActRecordDto = {
            template: data.templateId,
            patient: patientId,
            performedBy: data.performedById,
            priceAtTime: data.priceAtTime,
            performedAt: new Date(data.performedAt).toISOString(),
            notes: data.notes || undefined,
            ...(consultationId && { consultation: consultationId }),
            ...(expertiseId && { expertise: expertiseId }),
        }

        try {
            await createRecord(dto).unwrap()
            onSuccess?.()
            onClose()
        } catch (error) {
            // Error handled by RTK Query
        }
    }

    const activeTemplates = templates?.filter(t => t.isActive) || []
    const availableStaff = staffList || []
    const isLoading = isLoadingTemplates || isLoadingStaff

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle>
                    {t('medicalAct.record.title', { defaultValue: 'Record Medical Act' })}
                </DialogTitle>
                <DialogContent>
                    {createError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {t('common.error.save', { defaultValue: 'Error saving data' })}
                        </Alert>
                    )}

                    {isLoading ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            {/* Template Selection */}
                            <Grid item xs={12}>
                                <TemplateAutocomplete
                                    templates={activeTemplates}
                                    control={control}
                                    errors={errors}
                                />
                            </Grid>

                            {/* Selected template info */}
                            {selectedTemplate && (
                                <Grid item xs={12}>
                                    <SelectedTemplateInfo template={selectedTemplate} />
                                </Grid>
                            )}

                            {/* Performer Selection */}
                            <Grid item xs={12}>
                                <PerformerSelect
                                    staffList={availableStaff}
                                    control={control}
                                    errors={errors}
                                />
                            </Grid>

                            {/* Price Override (optional) */}
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="priceAtTime"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type="number"
                                            label={t('medicalAct.record.price', { defaultValue: 'Price' })}
                                            fullWidth
                                            inputProps={{ min: 0 }}
                                            onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                                            helperText={t('medicalAct.record.priceHelper', { defaultValue: 'Leave empty to use template price' })}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Performed At */}
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="performedAt"
                                    control={control}
                                    rules={{ required: t('common.validation.required', { defaultValue: 'This field is required' }) }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type="datetime-local"
                                            label={t('medicalAct.record.performedAt', { defaultValue: 'Performed At' })}
                                            fullWidth
                                            required
                                            InputLabelProps={{ shrink: true }}
                                            error={!!errors.performedAt}
                                            helperText={errors.performedAt?.message}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Notes */}
                            <Grid item xs={12}>
                                <Controller
                                    name="notes"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label={t('common.notes', { defaultValue: 'Notes' })}
                                            fullWidth
                                            multiline
                                            rows={2}
                                            placeholder={t('medicalAct.record.notesPlaceholder', { defaultValue: 'Optional notes about this act' })}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Context Info */}
                            <Grid item xs={12}>
                                <ContextInfoBanner
                                    consultationId={consultationId}
                                    expertiseId={expertiseId}
                                />
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={isCreating}>
                        {t('common.cancel', { defaultValue: 'Cancel' })}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isCreating || isLoading}
                    >
                        {isCreating ? <CircularProgress size={20} /> : t('medicalAct.record.submit', { defaultValue: 'Record Act' })}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}

export default RecordMedicalActModal
